<template>
  <v-dialog
    v-model="internalModelValue"
    width="550px"
    :fullscreen="isMobile"
  >
    <v-card>
      <v-card-title class="edit-material-dialog__header">
        <span>{{ $t('ad-materials.edit.title') }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          color="white"
          @click="close"
        />
      </v-card-title>
      <v-card-text class="pt-6">
        <p class="text-body-2 mb-4">
          {{ $t('ad-materials.edit.description') }}
        </p>
        <v-select
          v-model="selectedTopicId"
          :items="topics"
          :label="$t('ad-materials.edit.topic-label')"
          item-title="name"
          item-value="id"
          variant="outlined"
          density="comfortable"
          :disabled="isUpdating"
        />
        <v-text-field
          v-model="materialTitle"
          :label="$t('ad-materials.edit.title-label')"
          variant="outlined"
          density="comfortable"
          :disabled="isUpdating"
        />
      </v-card-text>
      <v-card-actions
        :class="{
          'edit-material-dialog__actions-padding': isMobile,
        }"
      >
        <v-btn
          color="grey-darken-3"
          variant="text"
          @click="close"
          :disabled="isUpdating"
        >
          {{ $t('labels.close') }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          @click="save"
          :loading="isUpdating"
          :disabled="!canSave"
        >
          {{ $t('labels.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import * as Sentry from '@sentry/vue'
import { useMutation } from '@vue/apollo-composable'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

import { showSnackbarMessage } from '@/lib/snackbarMessages'

import type { FlattenedMaterial } from './index.vue'
import UPDATE_USER_UPLOADED_MATERIAL from './UpdateUserUploadedMaterial.gql'

interface Topic {
  id: string
  name: string
}

type Props = {
  modelValue: boolean
  material: FlattenedMaterial | null
  topics: Topic[]
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'material-updated'): void
}>()

const { t } = useI18n()
const { smAndDown: isMobile } = useDisplay()

// Local state
const selectedTopicId = ref<string>('')
const materialTitle = ref<string>('')

// Apollo mutation
const { mutate: updateMaterial, loading: isUpdating } = useMutation(UPDATE_USER_UPLOADED_MATERIAL)

const internalModelValue = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const canSave = computed(() => {
  return !!selectedTopicId.value
})

// Watch for material changes to populate form
watch(
  () => props.material,
  (newMaterial) => {
    if (newMaterial) {
      selectedTopicId.value = newMaterial.topicId || ''
      materialTitle.value = newMaterial.displayName || ''
    }
  },
  { immediate: true }
)

const close = () => {
  emit('update:modelValue', false)
}

const save = async () => {
  if (!props.material || !canSave.value) return

  try {
    const result = await updateMaterial({
      input: {
        materialId: props.material.id,
        topicId: selectedTopicId.value,
        displayName: materialTitle.value.trim() || props.material.displayName,
      },
    })

    if (result?.data?.updateUserUploadedMaterial) {
      showSnackbarMessage('success', t('ad-materials.edit.success'))
      emit('material-updated')
      close()
    } else {
      throw new Error('Update failed')
    }
  } catch (error: any) {
    Sentry.captureException(error, {
      tags: {
        component: 'EditMaterialDialog',
        action: 'save',
        materialId: props.material?.id,
      },
    })
    showSnackbarMessage('error', t('ad-materials.edit.error'))
  }
}
</script>

<style scoped>
.edit-material-dialog__header {
  background-color: rgb(var(--v-theme-primary));
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.edit-material-dialog__actions-padding {
  padding-bottom: 5rem;
}
</style>
